import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Wrench, Settings2, Volume2, Speaker, Tv, Cable, Home, Bot, ArrowRight, CheckCircle2 } from "lucide-react";

const SERVICES = [
  { i: Home, t: "Home Theatre Design", d: "Room layout, sightlines, seating, acoustic modeling." },
  { i: Volume2, t: "Room Calibration", d: "Dirac Live, Anthem ARC, Audyssey XT32 measurements." },
  { i: Speaker, t: "Dolby Atmos Installation", d: "In-ceiling and elevation modules configured to spec." },
  { i: Settings2, t: "Acoustic Treatment", d: "Bass traps, diffusors, custom fabric panels." },
  { i: Tv, t: "Projector Installation", d: "Mounting, throw-distance optimisation, screen tensioning." },
  { i: Cable, t: "Cable Management", d: "Concealed conduit, banana plugs, HDMI 2.1 certified." },
  { i: Bot, t: "Smart Home Integration", d: "Control4, Crestron, RTI, Lutron scenes." },
  { i: Wrench, t: "Room Automation", d: "Motorised screens, lighting, curtains, HVAC control." },
];

export default function Installation() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/inquiries", { ...form, subject: "Installation site visit", kind: "booking" });
    toast.success("Booking received. Our team will call you within 24 hours.");
    setForm({ name: "", email: "", phone: "", message: "" });
  };
  return (
    <div className="pt-32 pb-24" data-testid="installation-page">
      <section className="container-x">
        <div className="max-w-3xl">
          <div className="kicker mb-3">Installation Services</div>
          <h1 className="font-display text-5xl md:text-7xl">White-glove <em className="not-italic gold-text">craftsmanship.</em></h1>
          <p className="mt-6 text-white/60 leading-relaxed max-w-xl">From acoustic modeling to final calibration, every project is delivered by our in-house engineers with reference-grade tools.</p>
        </div>
      </section>
      <section className="container-x mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {SERVICES.map(({ i: I, t, d }) => (
          <div key={t} className="card-luxury p-7">
            <div className="w-11 h-11 rounded-xl glass flex items-center justify-center text-gold mb-5"><I className="w-5 h-5" /></div>
            <h3 className="font-display text-xl mb-2">{t}</h3>
            <p className="text-sm text-white/55 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>
      <section className="container-x mt-24">
        <div className="glass rounded-3xl p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="kicker mb-3">Book a Site Visit</div>
            <h2 className="font-display text-3xl md:text-4xl">Let's design your <em className="not-italic gold-text">reference room.</em></h2>
            <ul className="mt-6 space-y-2.5 text-sm text-white/70">
              {["On-site room measurement","Bespoke system proposal","Fixed timeline & pricing","Full warranty coverage"].map((l) => (
                <li key={l} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold" />{l}</li>
              ))}
            </ul>
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="install-form">
            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="install-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="install-email" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="install-phone" />
            <textarea rows="4" placeholder="Tell us about your room and vision…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="install-message"></textarea>
            <button type="submit" className="btn-gold w-full justify-center" data-testid="install-submit">Book Site Visit <ArrowRight className="w-4 h-4" /></button>
          </form>
        </div>
      </section>
    </div>
  );
}
